/**
 * The shape of a home guide and the builder the guide files use (guides-food.ts, guides-rooms.ts). Every guide has
 * two forms (HOME.md): the full SOP, in sop.ts's SopSection shape and in the same order as sop.ts
 * (When · You need · Never · Steps · Where the supplies live), and an Easy procedure (easy.ts) that passes
 * lintProcedure. Easy steps re-say the SOP in plain words; they never add a rule.
 */
import type { SopSection } from "../sop.ts";
import { warningsFirst, type EasyIcon, type EasyProcedure, type EasyStep } from "../easy.ts";
import { hygiene, storage, clean, food, laundry, NOT_ENDORSED, type Citation } from "./sources.ts";
import { forbiddenIn, needsIn, purposeById, type ProductPurposeId } from "../products.ts";

/** "Never" sits before the steps (owner, 2026-10-05): a warning read after the step it warns about is read too late. */
export const HOME_SOP_ORDER = ["When", "You need", "Never", "Steps", "Where the supplies live"] as const;

/**
 * One line of a guide's "You need" list and what to buy for it. A plain string is matched by the catalog's own words
 * (products.ts needsIn); `buy` names the catalog purposes when the words don't (or name the wrong one); `noBuy` says why
 * there is nothing to buy ("the care label on your item", "tap water"). test/guide-needs.test.ts: every line has one or the other.
 */
export type NeedLine = string | { say: string; buy?: ProductPurposeId | readonly ProductPurposeId[]; noBuy?: string };
export interface NeedLineInfo { say: string; buy: ProductPurposeId[]; noBuy?: string }

/**
 * The Amazon searches under a "You need" line: one per catalog purpose it buys (the same product and search as the step's chip),
 * so the line and the step offer the same thing. Nothing for a line with nothing to buy.
 */
export const needLineSearches = (line: NeedLineInfo | undefined): { label: string; query: string }[] =>
  (line?.buy ?? []).map((id) => { const p = purposeById(id); return { label: p.name, query: p.default.query }; });

export interface HomeProduct { job: string; examples: string[]; citations: Citation[]; note?: string }

export interface HomeGuide {
  id: string;
  title: string;
  /** "chore" guides share their id with a chore (chores.ts sopId); "topic" guides stand alone (one-sink prep, bed making…). */
  kind: "chore" | "topic";
  sections: SopSection[];
  /** The "You need" lines with what to buy for each (the same lines as sections[1]). */
  needLines: NeedLineInfo[];
  easy: EasyProcedure;
  citations: Citation[];
  /** Named by a source; always shown with NOT_ENDORSED. */
  products: HomeProduct[];
  productNote: string;
  /** Where the sources are silent on part of this guide: the reason, shown to people. */
  suggested?: { reason: string };
  keywords: string[];
  /** Ask picks this guide only when the question matches (so "load the washer" or "rust on the sink" can't land on
   * "Load the dishwasher" or the cast-iron rust guide just by sharing one word). */
  askMatch?: RegExp;
  /**
   * The 5-minute version: which Easy steps are the essential first ones (0-based indexes into `easy.steps`). Only
   * steps already in the guide, never new words (the quick player shows exactly those steps). `fullIf` names when to do
   * the full version instead, quoted word for word from this guide's When or Steps lines. Absent = no short version
   * (the button is hidden; we don't invent one).
   */
  quick?: { steps: number[]; fullIf?: string };
}

export interface GuideSpec {
  id: string;
  title: string;
  kind?: "chore" | "topic";
  when: string[];
  need: NeedLine[];
  steps: string[];
  where: string[];
  never: string[];
  easy: { title: string; why: string; steps: EasyStep[] };
  citations: Citation[];
  products?: HomeProduct[];
  suggested?: string;
  keywords?: string[];
  askMatch?: RegExp;
  quick?: HomeGuide["quick"];
}

/**
 * "What you'll need" lists every purpose a "You need" line buys (stepsupplies.ts reads a step's `needs` into the list).
 * A purpose some step's words already name is left alone (the list has it, with that step's chip). The others ride on the
 * first step that doesn't forbid them: listed, with their own buy link on the page, but never a chip on a step that
 * isn't about them. Steps are copied, never changed in place.
 */
function attachNeeds(steps: EasyStep[], lines: NeedLineInfo[]): EasyStep[] {
  const out = steps.map((st) => ({ ...st }));
  const named = new Set(out.flatMap((st) => [...needsIn(st.say, { home: true, max: 99 }), ...(st.buys ?? [])]));
  for (const l of lines) for (const id of l.buy) {
    if (named.has(id) || out.some((st) => st.needs?.includes(id))) continue;
    const at = out.findIndex((st) => st.icon !== "stop" && !forbiddenIn(st.say, { home: true }).includes(id));
    if (at >= 0) out[at] = { ...out[at]!, needs: [...(out[at]!.needs ?? []), id] };
  }
  return out;
}

export function guide(g: GuideSpec): HomeGuide {
  const needLines: NeedLineInfo[] = g.need.map((n) => {
    if (typeof n === "string") return { say: n, buy: needsIn(n, { home: true, max: 6 }) };
    return { say: n.say, buy: n.buy === undefined ? [] : typeof n.buy === "string" ? [n.buy] : [...n.buy], ...(n.noBuy !== undefined ? { noBuy: n.noBuy } : {}) };
  });
  const tagged = attachNeeds(g.easy.steps, needLines);
  // Warnings first (easy.ts warningsFirst): the quick version's step numbers follow the steps to their new places.
  const steps = warningsFirst(tagged);
  const quick = g.quick ? { ...g.quick, steps: g.quick.steps.map((i) => steps.indexOf(tagged[i]!)).filter((i) => i >= 0) } : undefined;
  const sections: SopSection[] = [
    { title: "When", items: g.when },
    { title: "You need", items: needLines.map((l) => l.say) },
    { title: "Never", items: g.never },
    { title: "Steps", items: g.steps },
    { title: "Where the supplies live", items: g.where },
  ];
  const citations = [...new Set([...g.citations, ...(g.products ?? []).flatMap((p) => p.citations)])];
  return {
    id: g.id, title: g.title, kind: g.kind ?? "chore", sections, needLines,
    easy: { id: `easy-${g.id}`, title: g.easy.title, why: g.easy.why, for: {}, steps, citations },
    citations, products: g.products ?? [], productNote: NOT_ENDORSED,
    ...(g.suggested ? { suggested: { reason: g.suggested } } : {}),
    keywords: g.keywords ?? [],
    ...(g.askMatch ? { askMatch: g.askMatch } : {}),
    ...(quick ? { quick: { steps: [...quick.steps], ...(quick.fullIf ? { fullIf: quick.fullIf } : {}) } } : {}),
  };
}

/** One Easy step. */
export const s = (icon: EasyIcon, say: string, extra: Omit<EasyStep, "icon" | "say"> = {}): EasyStep => ({ icon, say, ...extra });

/** Shared "Where the supplies live" lines, each from a placement-table row (home-storage-organization). */
export const WHERE = {
  cleaners: "Cleaners and bleach: in their original labeled bottles, away from food and medicines; latched or up high where children live or visit (Poison Control, EPA).",
  bleachMix: "A bleach mix is made fresh each day and poured out after; never stored in a food or drink container (CDC, EPA).",
  cloths: "Cloths and sponges: dry them between uses; kitchen cloths never go to the bathroom (CDC color-coding, applied at home).",
  bathroomTools: "Bathroom brush, mop and cloths: kept with the bathroom, apart from kitchen ones; hang the mop head up to dry (CDC healthcare practice, applied at home).",
  towels: "Clean towels and linens: put away only when fully dry, in a closet with air moving; indoor humidity below 60% (EPA, MU Extension).",
  food: "Food: raw meat sealed on the bottom shelf; cans and dry goods cool, dry and off the floor, oldest in front (USDA FSIS, USU Extension).",
  filters: "Spare filters and descalers: in their sealed packs in a cool, dry cabinet, away from food; cleaners never above food (Poison Control).",
  laundry: "Detergent and bleach: original containers, lids closed, out of reach of children and pets (Poison Control, EPA).",
} as const;

/** Citations for the WHERE lines above. */
export const WHERE_CITES = {
  cleaners: storage("H31", "H32", "H34"),
  bleachMix: [...food("H28"), ...storage("H31")],
  cloths: clean("F12"),
  bathroomTools: clean("F12"),
  towels: storage("H38", "H39"),
  food: storage("H2", "H7", "H23"),
  filters: storage("H32"),
  laundry: storage("H31", "H32", "H33"),
} as const;

/** Shared "Never" lines. */
export const NEVER = {
  mixBleach: "Never mix bleach with ammonia, vinegar, acids or other cleaners: it makes toxic gas (CDC, Poison Control).",
  foodContainer: "Never put a cleaner or a bleach mix in a food or drink container (EPA, Poison Control).",
  shareRazor: "Never share razors, towels or washcloths (CDC, AAD).",
} as const;
export const NEVER_CITES = {
  mixBleach: [...clean("G9", "G10"), ...food("H28")],
  foodContainer: storage("H31", "H32"),
  shareRazor: hygiene("C8", "A1"),
} as const;

export { laundry as laundryCite };
